import { useEffect, useState } from 'react';
import { getExternalEmrSummary } from '../services/externalEmrPatients';
import { EXTERNAL_EMR_VIEW_SUMMARY_QUERY } from './useExternalEmrSummary';

export interface TakeOverPendingSignal {
  count: number;
  lastDay: string;
}

/**
 * The default view's one line (EMR-2915): how many charts still need a
 * person's Take Over before the last day. Reads the External EMR summary
 * once, and again when the clinic changes or the view returns. No polling;
 * a failed read shows nothing.
 */
export function useTakeOverPendingSignal({
  enabled,
  clinicId,
}: {
  enabled: boolean;
  clinicId: string | null;
}): TakeOverPendingSignal | null {
  const [signal, setSignal] = useState<TakeOverPendingSignal | null>(null);
  useEffect(() => {
    if (!enabled) {
      setSignal(null);
      return;
    }
    let live = true;
    getExternalEmrSummary({
      clinic_id: clinicId,
      ...EXTERNAL_EMR_VIEW_SUMMARY_QUERY,
    })
      .then(summary => {
        if (!live) return;
        const count = summary.take_over_pending ?? 0;
        setSignal(
          count > 0 && summary.last_day
            ? { count, lastDay: summary.last_day }
            : null
        );
      })
      .catch(error => {
        if (!live) return;
        console.error('Failed to read the Take Over count:', error);
        setSignal(null);
      });
    return () => {
      live = false;
    };
  }, [enabled, clinicId]);
  return signal;
}
